import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import BookingForm from '../components/BookingForm';
import FeaturedRoutePromotion from '../components/FeaturedRoutePromotion';
import SeoHead from '../components/SeoHead';
import { CUSTOMER_SUPPORT } from '../data/customerPolicies';
import {
  DRIVER_BATA,
  MIN_ONEWAY_KM,
  VEHICLES,
  getVehicleDisplayName,
} from '../data/bookingConfig';
import {
  describeCorridorCharges,
  formatInr,
  getPublishedFare,
} from '../data/localCorridorFares';
import '../styles/RouteLanding.css';

const BOOKING_ID = 'route-booking';
const OG_IMAGE = '/images/banner/easy-drop-taxi-hero.webp';

const scrollToBooking = () => {
  const target = document.getElementById(BOOKING_ID);
  if (target) {
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};

const whatsappHref = (page) => {
  const digits = CUSTOMER_SUPPORT.phoneTel.replace(/\D/g, '');
  const text = `Hello, I want a one-way taxi from ${page.origin} to ${page.destination}.`;
  return `https://wa.me/${digits}?text=${encodeURIComponent(text)}`;
};

const LocalCorridorPage = ({ page }) => {
  const [preferredVehicle, setPreferredVehicle] = useState('');
  const [routePrefill, setRoutePrefill] = useState(null);
  const [websiteEstimate, setWebsiteEstimate] = useState(null);
  const chargeNotes = useMemo(() => describeCorridorCharges(page.tariff), [page.tariff]);
  const hasPublishedFare = Object.keys(page.tariff.vehicles).length > 0;
  const websiteEstimateOnly = !page.tariff.oneWayPackage && !hasPublishedFare;
  const comparisonVehicles = page.tariff.oneWayPackage
    ? VEHICLES.filter((vehicle) => page.tariff.vehicles[vehicle.type] != null)
    : VEHICLES;

  useEffect(() => {
    setWebsiteEstimate(null);
    setPreferredVehicle('');
    setRoutePrefill(null);
  }, [page.slug]);

  const jsonLd = useMemo(() => {
    const offers = Object.entries(page.tariff.vehicles).map(([vehicleType, price]) => ({
      '@type': 'Offer',
      name: `${vehicleType} one-way taxi`,
      priceCurrency: 'INR',
      price,
      url: `https://easydroptaxi.com${page.slug}`,
      description: page.tariff.includedDistanceKm
        ? `One-way fare covering ${page.tariff.includedDistanceKm} km`
        : 'Published one-way fare',
    }));

    const service = {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: page.h1,
      serviceType: 'One-way taxi booking',
      provider: {
        '@type': 'TaxiService',
        name: 'Easy Drop Taxi',
        url: 'https://easydroptaxi.com/',
        telephone: '+919629954544',
      },
      areaServed: [page.origin, page.destination, 'Tamil Nadu'],
      url: `https://easydroptaxi.com${page.slug}`,
    };

    if (offers.length) service.offers = offers;

    return [
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://easydroptaxi.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: page.h1,
            item: `https://easydroptaxi.com${page.slug}`,
          },
        ],
      },
      service,
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: page.faqs.map((item) => ({
          '@type': 'Question',
          name: item.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.answer,
          },
        })),
      },
    ];
  }, [page]);

  const selectVehicle = (vehicleType) => {
    setPreferredVehicle(vehicleType);
    scrollToBooking();
  };

  return (
    <main className="route-landing">
      <SeoHead
        title={page.seoTitle}
        description={page.seoDescription}
        path={page.slug}
        image={OG_IMAGE}
        jsonLd={jsonLd}
      />

      <section className="rl-hero">
        <div className="rl-hero-inner">
          <div>
            <nav className="rl-breadcrumb" aria-label="Breadcrumb">
              <Link to="/">Home</Link>
              <span aria-hidden="true">/</span>
              <span>{page.h1}</span>
            </nav>
            <h1>{page.h1}</h1>
            <p className="rl-hero-lead">{page.heroLead}</p>
            <div className="rl-hero-note">
              <span><i className="fa fa-road" aria-hidden="true"></i>One Way Taxi</span>
              <span>
                <i className="fa fa-phone" aria-hidden="true"></i>
                <a href={CUSTOMER_SUPPORT.phoneTel}>{CUSTOMER_SUPPORT.phoneDisplay}</a>
              </span>
            </div>
            <div className="rl-hero-actions">
              <button type="button" onClick={scrollToBooking}>Book This Route</button>
              <a href={CUSTOMER_SUPPORT.phoneTel}>Call</a>
              <a href={whatsappHref(page)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
            </div>
          </div>

          <div id={BOOKING_ID} className="rl-booking-card">
            <BookingForm
              key={page.slug}
              initialPickup={routePrefill?.pickup || page.origin}
              initialDrop={routePrefill?.drop || page.destination}
              initialTripType="One Way"
              initialVehicle={preferredVehicle}
              searchButtonLabel="Get Fare Estimate"
              oneWayPackage={
                page.tariff.oneWayPackage
                && (!routePrefill || (routePrefill.pickup === page.origin && routePrefill.drop === page.destination))
                  ? page.tariff
                  : null
              }
              labelWebsiteEstimate={websiteEstimateOnly}
              onWebsiteEstimate={websiteEstimateOnly ? setWebsiteEstimate : null}
            />
          </div>
        </div>
      </section>

      <section className="rl-strip" aria-label="Route information">
        <div className="container">
          <div className="rl-strip-grid">
            <div className="rl-strip-item">
              <span className="rl-strip-label">Route</span>
              <span className="rl-strip-value">{page.origin} → {page.destination}</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">Trip Type</span>
              <span className="rl-strip-value">One Way Taxi</span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">{websiteEstimateOnly ? 'Fixed package' : 'Included Distance'}</span>
              <span className="rl-strip-value">
                {page.tariff.includedDistanceKm
                  ? `${page.tariff.includedDistanceKm} km`
                  : 'None for this direction'}
              </span>
            </div>
            <div className="rl-strip-item">
              <span className="rl-strip-label">{websiteEstimateOnly ? 'Estimate rate' : 'Extra Kilometre'}</span>
              <span className="rl-strip-value">
                {page.tariff.additionalKmRate
                  ? `${formatInr(page.tariff.additionalKmRate)} / km`
                  : websiteEstimateOnly
                    ? `Sedan ₹${VEHICLES.find((v) => v.type === 'Sedan').rate}, SUV ₹${VEHICLES.find((v) => v.type === 'SUV').rate}, Crysta ₹${VEHICLES.find((v) => v.type === 'MUV').rate} / km`
                    : 'Not published'}
              </span>
            </div>
            {websiteEstimate && (
              <div className="rl-strip-item">
                <span className="rl-strip-label">Billed distance</span>
                <span className="rl-strip-value">{websiteEstimate.billedKm} km estimate</span>
              </div>
            )}
            {page.tariff.extraCharges?.length > 0 && Object.entries(page.tariff.vehicles).map(([vehicleType, amount]) => (
              <div className="rl-strip-item" key={vehicleType}>
                <span className="rl-strip-label">{getVehicleDisplayName(vehicleType)}</span>
                <span className="rl-strip-value">{formatInr(amount)} + toll and parking</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <FeaturedRoutePromotion
        origin={page.origin}
        destination={page.destination}
        onGetFareEstimate={({ origin, destination, vehicleType }) => {
          setRoutePrefill({ pickup: origin, drop: destination });
          setPreferredVehicle(vehicleType);
          const target = document.getElementById(BOOKING_ID);
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
      />

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Vehicle Fare Comparison</h2>
          <p className="rl-section-intro">
            {page.tariff.oneWayPackage
              ? 'These one-way package fares apply only when One Way Drop is selected. Round Trip inside the booking card uses the website’s normal round-trip fare.'
              : hasPublishedFare
                ? 'Published one-way fares are shown only where a tariff was confirmed. Other vehicles stay unpublished instead of using a guessed price.'
                : 'No fixed one-way total is published for this route. Compare cabins here, then use the booking form to estimate or contact Easy Drop Taxi.'}
          </p>
          <div className="rl-vehicle-grid">
            {comparisonVehicles.map((vehicle) => {
              const published = getPublishedFare(page.tariff, vehicle.type);
              const estimate = websiteEstimate?.vehicles.find((item) => item.type === vehicle.type);
              return (
                <article key={vehicle.type} className="rl-vehicle-card">
                  <div className="rl-vehicle-media">
                    <img
                      src={vehicle.fleetImage || vehicle.image}
                      alt={`${getVehicleDisplayName(vehicle)} taxi option for ${page.origin} to ${page.destination}`}
                      width="320"
                      height="160"
                    />
                  </div>
                  <div className="rl-vehicle-body">
                    <h3>{getVehicleDisplayName(vehicle)}</h3>
                    <p>{vehicle.description}</p>
                    <div className="rl-vehicle-meta">
                      <span><i className="fa fa-users" aria-hidden="true"></i>{vehicle.seats}</span>
                      <span>
                        <i className="fa fa-tag" aria-hidden="true"></i>
                        {page.tariff.extraCharges?.length
                          ? `${page.tariff.includedDistanceKm} KM included`
                          : page.tariff.oneWayPackage
                            ? `Extra ${formatInr(page.tariff.additionalKmRate)}/km after ${page.tariff.includedDistanceKm} km`
                            : `Estimate ₹${vehicle.rate}/km, not a package`}
                      </span>
                    </div>
                    {published != null ? (
                      <p>
                        {page.tariff.extraCharges?.length ? 'Package fare' : 'Published one-way fare'}: {formatInr(published)}
                      </p>
                    ) : estimate ? (
                      <p>Fare estimate: {formatInr(estimate.total)}. Not a fixed package.</p>
                    ) : (
                      <p>Fare estimate: measure the route in the booking form. Not a fixed package.</p>
                    )}
                    {page.tariff.extraCharges?.length > 0 && published != null && (
                      <>
                        <p>Included distance: {page.tariff.includedDistanceKm} KM</p>
                        <p>Additional distance: {formatInr(page.tariff.additionalKmRate)}/KM</p>
                        <p>Toll and parking: Extra</p>
                      </>
                    )}
                    <button
                      type="button"
                      className={`rl-select-btn${preferredVehicle === vehicle.type ? ' is-selected' : ''}`}
                      onClick={() => selectVehicle(vehicle.type)}
                    >
                      {preferredVehicle === vehicle.type ? 'Selected' : 'Book This Vehicle'}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {page.sections.map((section) => (
        <section className="rl-section" key={section.heading}>
          <div className="container">
            <h2>{section.heading}</h2>
            <div className="rl-prose">
              {section.paragraphs.map((paragraph) => (
                <p key={paragraph}>{paragraph}</p>
              ))}
            </div>
          </div>
        </section>
      ))}

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Fare Details</h2>
          <div className="rl-fare-grid">
            {comparisonVehicles.map((vehicle) => {
              const published = getPublishedFare(page.tariff, vehicle.type);
              const estimate = websiteEstimate?.vehicles.find((item) => item.type === vehicle.type);
              return (
                <article key={vehicle.type} className="rl-fare-card">
                  <h3>{getVehicleDisplayName(vehicle)}</h3>
                  <p>
                    {published != null
                      ? formatInr(published)
                      : estimate
                        ? `Estimate ${formatInr(estimate.total)}`
                        : 'Estimate after the route is measured'}
                  </p>
                  <p>
                    {page.tariff.extraCharges?.length
                      ? `Included distance: ${page.tariff.includedDistanceKm} KM. Additional distance: ${formatInr(page.tariff.additionalKmRate)}/KM. Toll and parking: Extra. Round Trip uses the normal website fare.`
                      : page.tariff.oneWayPackage
                        ? 'One-way package. Round Trip uses the normal website fare.'
                        : `Website estimate at ₹${vehicle.rate}/km plus ₹${DRIVER_BATA} driver bata. The one-way billed distance is at least ${MIN_ONEWAY_KM} km. This is not a fixed package.`}
                  </p>
                </article>
              );
            })}
          </div>
          <div className="rl-prose">
            {chargeNotes.map((note) => (
              <p key={note}>{note}</p>
            ))}
            {page.tariff.oneWayPackage ? (
              <p>
                Choose One Way Drop to book this package. Choose Round Trip and the form returns to the existing
                round-trip distance, vehicle list, and charges, including Crysta where the website already offers it.
              </p>
            ) : websiteEstimateOnly ? (
              <p>
                The booking form and these cards show the same website estimate. One-way estimates use a {MIN_ONEWAY_KM} km
                minimum, the vehicle rate, and ₹{DRIVER_BATA} driver bata. This is not a fixed package. Read the{' '}
                <Link to="/fare-payment-policy">Fare &amp; Payment Policy</Link> for the general rules.
              </p>
            ) : (
              <p>
                The booking form is unchanged. One-way estimates there use a {MIN_ONEWAY_KM} km minimum,
                the vehicle rate, and ₹{DRIVER_BATA} driver bata. Read the{' '}
                <Link to="/fare-payment-policy">Fare &amp; Payment Policy</Link> for the general rules.
              </p>
            )}
          </div>
          <button type="button" className="rl-select-btn rl-inline-cta" onClick={scrollToBooking}>
            {hasPublishedFare ? 'Book With This Route Filled In' : 'Get Fare Estimate'}
          </button>
        </div>
      </section>

      <section className="rl-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="rl-faq">
            {page.faqs.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-section rl-section-muted">
        <div className="container">
          <h2>Related Routes</h2>
          <p className="rl-section-intro">
            Going the other way?{' '}
            <Link to={page.reverseSlug}>{page.destination} to {page.origin} One Way Taxi</Link>
          </p>
          <div className="rl-links">
            {page.related.map((link) => (
              <Link key={link.path} to={link.path}>{link.label}</Link>
            ))}
          </div>
        </div>
      </section>

      <section className="rl-cta">
        <div className="container">
          <h2>Book a {page.origin} to {page.destination} taxi</h2>
          <p>Pickup and drop are filled in on the booking card. Call or WhatsApp if you want the fare confirmed first.</p>
          <div className="rl-hero-actions rl-cta-actions">
            <button type="button" onClick={scrollToBooking}>Continue Booking</button>
            <a href={CUSTOMER_SUPPORT.phoneTel}>Call {CUSTOMER_SUPPORT.phoneDisplay}</a>
            <a href={whatsappHref(page)} target="_blank" rel="noopener noreferrer">WhatsApp</a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default LocalCorridorPage;
